import { Component, ViewChild, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { finalize } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApiService } from 'src/app/services/api.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService } from 'src/app/services/role-loading.service'
import { ApiKeyDto } from 'src/app/services/dtos'
import { ApiKeyManagementItem } from '../api-key-management/api-key-management.component'
import { ModalService, ModalController } from '../../services/modal.service'
import { handleArpioError } from 'src/utils/handle-error'
import { ApiKeyModalBaseComponent } from '../api-key-modal-base/api-key-modal-base.component'

@Component({
  selector: 'app-edit-api-key-modal',
  templateUrl: './edit-api-key-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditApiKeyModalComponent extends ApiKeyModalBaseComponent implements ModalController {
  @ViewChild('editApiKeyModal') modal: ModalDialogComponent
  @Output() apiKeyUpdated = new EventEmitter<ApiKeyManagementItem>()

  // Current API key being edited
  currentApiKey: ApiKeyManagementItem | null = null
  currentApiKeyDetails: ApiKeyDto | null = null

  constructor(
    formBuilder: FormBuilder,
    accountService: AccountService,
    uiService: UiService,
    apiService: ApiService,
    accessLevelService: AccessLevelService,
    roleLoadingService: RoleLoadingService,
    private modalService: ModalService
  ) {
    super(formBuilder, accountService, uiService, apiService, accessLevelService, roleLoadingService)
    this.modalService.register(this, this)
  }

  ngOnDestroy() {
    super.ngOnDestroy()
    this.modalService.unregister(this)
  }

  async open(apiKey: ApiKeyManagementItem) {
    this.currentApiKey = apiKey
    this.resetForm()
    await this.loadDataForEdit()
    this.modal.open()
  }

  protected resetForm() {
    super.resetForm()
    this.currentApiKeyDetails = null
  }

  private async loadDataForEdit(): Promise<void> {
    if (!this.currentApiKey) return

    this.loadingRoles = true
    try {
      // Load roles and API key details in parallel
      const [roles, apiKeyDetails] = await Promise.all([
        this.roleLoadingService.loadRoles(),
        this.apiService.getApiKey(this.accountService.accountId, this.currentApiKey.id).toPromise(),
      ])

      this.availableRoles = roles
      this.currentApiKeyDetails = apiKeyDetails

      // Populate form with current values
      this.apiKeyForm.patchValue({
        name: apiKeyDetails.name || '',
        enabled: apiKeyDetails.enabled !== false,
      })

      // Select current roles
      if (apiKeyDetails.roles && apiKeyDetails.roles.length > 0) {
        this.selectedRoles = this.availableRoles.filter((role) => apiKeyDetails.roles.includes(role.id))
      }
    } catch (error) {
      console.error('Failed to load data for edit:', error)
      this.availableRoles = []
    } finally {
      this.loadingRoles = false
    }
  }

  // Save operation
  onSave() {
    if (!this.isFormValid || this.saving || !this.currentApiKey) {
      return
    }

    this.saving = true

    const formValue = this.apiKeyForm.value
    const apiKeyPayload: ApiKeyDto = {
      name: formValue.name,
      enabled: formValue.enabled !== false,
      roles: this.selectedRoles.map((r) => r.id),
    }

    this.saveSubscription = this.apiService
      .updateApiKey(this.accountService.accountId, this.currentApiKey.id, apiKeyPayload)
      .pipe(
        finalize(() => {
          this.saving = false
        })
      )
      .subscribe(
        (result: ApiKeyDto) => {
          this.apiKeyUpdated.emit(this.currentApiKey)
          this.close()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService, {
          errorContext: { dialogId: 'edit-api-key-modal' },
        })
      )
  }

  getSaveButtonText(): string {
    return this.saving ? 'Saving...' : 'Save Changes'
  }
}
